import {
  STAFFING_FLAT_TIER,
  matchRoleKey,
  type AiRosterSource,
  type SaveStaffingRequest,
  type StaffingPlanView,
  type StaffingSettings,
  type StaffingShareMap,
} from '@protoss/shared';
import { Button, NumberInput, SkeletonBlock } from '@protoss/ui';
import * as React from 'react';
import { apiFetch } from '../../lib/apiClient';
import { categoryLabel, labelize } from './labels';

/**
 * StaffingGrid - which roles staff a product's deals and how the hours split between them
 * (COS-TBD client-staffing-knobs). One table per resource plan (a plan can serve several
 * deal categories). A role is staffed when it is ticked with a share above 0; there is no
 * other on/off store. Save is blocked until every column adds up to 100% and every staffed
 * role has a rate - the server re-checks both with the shared rate-coverage rule.
 *
 * `ai` plans have no split: the AI staffs each deal from the category's roster (plan roles
 * plus roles with a description for that category). Those show the roster per category,
 * read-only, each role linked to its description in the roles list
 * (COS-TBD per-category-role-descriptions).
 */

/** Opens a role's description for one category in the roles list. */
export type EditDescriptionHandler = (roleKey: string, categoryKey: string | null) => void;

const ROSTER_SOURCE_LABEL: Record<AiRosterSource, string> = {
  plan: 'Listed in the plan',
  description: 'Has a description for this category',
  both: 'Listed in the plan, has a description',
};

/** Parse a NumberInput display value into a whole percent (0-100). */
function toShare(value: string): number {
  const n = parseInt(value.replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
}

interface Draft {
  variesBySize: boolean;
  /** Ticked roles (staffed). A ticked role needs a share above 0. */
  staffed: Set<string>;
  /** Tier -> role -> share. When !variesBySize only the first tier is edited and copied. */
  shares: Record<string, StaffingShareMap>;
}

function draftFromPlan(plan: StaffingPlanView): Draft {
  const staffed = new Set<string>();
  for (const t of plan.tiers)
    for (const [k, v] of Object.entries(plan.shares[t] ?? {})) if (v > 0) staffed.add(k);
  return {
    variesBySize: plan.variesBySize,
    staffed,
    shares: Object.fromEntries(plan.tiers.map((t) => [t, { ...(plan.shares[t] ?? {}) }])),
  };
}

/** The tiers the editor shows as columns. */
function columns(plan: StaffingPlanView, draft: Draft): string[] {
  return draft.variesBySize ? plan.tiers : plan.tiers.slice(0, 1);
}

/** What the save sends: every tier, only ticked roles with a share above 0. */
function requestShares(plan: StaffingPlanView, draft: Draft): SaveStaffingRequest['shares'] {
  const first = plan.tiers[0] ?? STAFFING_FLAT_TIER;
  return Object.fromEntries(
    plan.tiers.map((t) => {
      const src = draft.shares[draft.variesBySize ? t : first] ?? {};
      return [
        t,
        Object.fromEntries(Object.entries(src).filter(([k, v]) => draft.staffed.has(k) && v > 0)),
      ];
    })
  );
}

function PlanTable({
  plan,
  settings,
  productKey,
  onSaved,
  onEditDescription,
}: {
  plan: StaffingPlanView;
  settings: StaffingSettings;
  productKey: string;
  onSaved: (next: StaffingSettings) => void;
  onEditDescription?: EditDescriptionHandler;
}) {
  const [draft, setDraft] = React.useState<Draft>(() => draftFromPlan(plan));
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    setDraft(draftFromPlan(plan));
  }, [plan]);

  const overrides = new Set(plan.overrideRoleKeys);
  const rateByKey = new Map(settings.roles.map((r) => [r.roleKey, r]));
  // Catalog roles first, then any role the plan staffs that has no rate row.
  const planKeys = plan.tiers.flatMap((t) => Object.keys(plan.shares[t] ?? {}));
  const rows = [
    ...settings.roles.map((r) => r.roleKey),
    ...[...new Set(planKeys)].filter((k) => !rateByKey.has(k)),
  ].filter((k) => !overrides.has(k));

  // Role names that appear more than once (e.g. three "Functional Consultant" levels).
  const nameCounts = new Map<string, number>();
  for (const r of settings.roles) nameCounts.set(r.roleName, (nameCounts.get(r.roleName) ?? 0) + 1);
  const sameName = new Set([...nameCounts].filter(([, n]) => n > 1).map(([name]) => name));
  const roleLabel = (k: string): string => {
    const r = rateByKey.get(k);
    if (!r) return labelize(k);
    return r.level && sameName.has(r.roleName) ? `${r.roleName} (${r.level})` : r.roleName;
  };

  const cols = columns(plan, draft);
  const totals = Object.fromEntries(
    cols.map((t) => [
      t,
      Object.entries(draft.shares[t] ?? {}).reduce(
        (s, [k, v]) => s + (draft.staffed.has(k) ? v : 0),
        0
      ),
    ])
  );

  const unpriced = [...draft.staffed].filter((k) => {
    const r = rateByKey.get(k);
    return !r || (r.onshore <= 0 && r.offshore <= 0);
  });
  const noShare = [...draft.staffed].filter((k) => cols.every((t) => !(draft.shares[t]?.[k] ?? 0)));
  const badTotals = cols.filter((t) => totals[t] !== 100);
  const dirty =
    JSON.stringify(requestShares(plan, draft)) !==
    JSON.stringify(requestShares(plan, draftFromPlan(plan)));
  const canSave =
    plan.editable &&
    dirty &&
    !saving &&
    unpriced.length === 0 &&
    noShare.length === 0 &&
    badTotals.length === 0;

  function toggle(roleKey: string, on: boolean) {
    setSaved(false);
    setDraft((d) => {
      const staffed = new Set(d.staffed);
      if (on) staffed.add(roleKey);
      else staffed.delete(roleKey);
      return { ...d, staffed };
    });
  }

  function setShare(tier: string, roleKey: string, value: number) {
    setSaved(false);
    setDraft((d) => ({
      ...d,
      shares: { ...d.shares, [tier]: { ...(d.shares[tier] ?? {}), [roleKey]: value } },
    }));
  }

  function setVaries(varies: boolean) {
    setSaved(false);
    setDraft((d) => {
      const first = d.shares[plan.tiers[0] ?? ''] ?? {};
      // Turning it on starts every size from the shared split; turning it off keeps the first size's.
      const shares = Object.fromEntries(plan.tiers.map((t) => [t, { ...first }]));
      return { ...d, variesBySize: varies, shares };
    });
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const body: SaveStaffingRequest = {
        categoryKey: plan.categoryKey,
        shares: requestShares(plan, draft),
      };
      const res = await apiFetch<{ settings: StaffingSettings }>(
        `/api/workspace/admin/staffing?productKey=${encodeURIComponent(productKey)}`,
        { method: 'PUT', body: JSON.stringify(body) }
      );
      setSaved(true);
      onSaved(res.settings);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save the staffing.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="rr-staffing__plan"
      aria-label={`Staffing for ${categoryLabel(plan.categoryKeys)}`}
    >
      <div className="rr-staffing__plan-header">
        <h4 className="rr-staffing__plan-title">{categoryLabel(plan.categoryKeys)}</h4>
        {plan.editable && plan.tiers.length > 1 && (
          <label className="rr-staffing__toggle">
            <input
              type="checkbox"
              aria-label="Vary by deal size"
              checked={draft.variesBySize}
              onChange={(e) => setVaries(e.target.checked)}
              disabled={saving}
            />
            Vary by deal size
          </label>
        )}
      </div>

      {!plan.editable ? (
        <>
          <p className="rr-staffing__note">
            AI picks who works on each deal from the roles below, based on what each role&apos;s
            description says it does. To add a role here, give it a description for this deal type
            in the roles list above.
          </p>
          {plan.aiRosters.map((roster) => (
            <div className="rr-staffing__roster" key={roster.categoryKey ?? 'all'}>
              {plan.aiRosters.length > 1 && (
                <h5 className="rr-staffing__roster-title">{categoryLabel([roster.categoryKey])}</h5>
              )}
              {roster.roles.length === 0 ? (
                <p className="rr-staffing__note">No roles yet.</p>
              ) : (
                <table className="rr-staffing__table">
                  <thead>
                    <tr>
                      <th scope="col">Role</th>
                      <th scope="col">On the roster because</th>
                      <th scope="col">Rate</th>
                      <th scope="col">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roster.roles.map(({ roleKey, source }) => {
                      // `-`/`_` spellings are the same role (as coverage and pricing treat them).
                      const catalogKey = matchRoleKey(roleKey, rateByKey.keys()).match ?? roleKey;
                      const role = rateByKey.get(catalogKey);
                      const noRate = !role || (role.onshore <= 0 && role.offshore <= 0);
                      return (
                        <tr key={roleKey}>
                          <th scope="row" className="rr-staffing__role">
                            {roleLabel(catalogKey)}
                          </th>
                          <td className="rr-staffing__source">{ROSTER_SOURCE_LABEL[source]}</td>
                          <td
                            className={
                              noRate
                                ? 'rr-staffing__rate rr-staffing__rate--missing'
                                : 'rr-staffing__rate'
                            }
                          >
                            {!role
                              ? 'Not in the roles above'
                              : `$${role.onshore.toLocaleString('en-US')} / $${role.offshore.toLocaleString('en-US')}`}
                          </td>
                          <td className="rr-staffing__action">
                            {role && onEditDescription && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onEditDescription(catalogKey, roster.categoryKey)}
                              >
                                Edit description
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </>
      ) : (
        <>
          <table className="rr-staffing__table">
            <thead>
              <tr>
                <th scope="col">Role</th>
                <th scope="col">Works on deals</th>
                {cols.map((t) => (
                  <th scope="col" key={t}>
                    {draft.variesBySize ? `${labelize(t)} %` : 'Share %'}
                  </th>
                ))}
                <th scope="col">Rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((roleKey) => {
                const role = rateByKey.get(roleKey);
                const on = draft.staffed.has(roleKey);
                const noRate = !role || (role.onshore <= 0 && role.offshore <= 0);
                return (
                  <tr key={roleKey} className={on ? 'rr-staffing__row--on' : undefined}>
                    <th scope="row" className="rr-staffing__role">
                      {role?.roleName ?? labelize(roleKey)}
                      {role?.level && sameName.has(role.roleName) && (
                        <span className="rr-staffing__role-level"> · {role.level}</span>
                      )}
                    </th>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`${roleLabel(roleKey)} staffs deals`}
                        checked={on}
                        onChange={(e) => toggle(roleKey, e.target.checked)}
                        disabled={saving}
                      />
                    </td>
                    {cols.map((t) => (
                      <td key={t}>
                        <NumberInput
                          aria-label={`${roleLabel(roleKey)} share${draft.variesBySize ? ` (${t})` : ''}`}
                          className="rr-staffing__share"
                          value={on ? String(draft.shares[t]?.[roleKey] ?? 0) : ''}
                          min={0}
                          max={100}
                          thousandSeparator={false}
                          disabled={!on || saving}
                          onChange={(e) => setShare(t, roleKey, toShare(e.target.value))}
                        />
                      </td>
                    ))}
                    <td
                      className={
                        on && noRate
                          ? 'rr-staffing__rate rr-staffing__rate--missing'
                          : 'rr-staffing__rate'
                      }
                    >
                      {!role
                        ? 'Not in the roles above'
                        : `$${role.onshore.toLocaleString('en-US')} / $${role.offshore.toLocaleString('en-US')}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td />
                {cols.map((t) => (
                  <td
                    key={t}
                    className={
                      totals[t] === 100
                        ? 'rr-staffing__total'
                        : 'rr-staffing__total rr-staffing__total--bad'
                    }
                  >
                    {totals[t]}%
                  </td>
                ))}
                <td />
              </tr>
            </tfoot>
          </table>

          {plan.overrideRoleKeys.length > 0 && (
            <p className="rr-staffing__note">
              Assigned whole sections, outside this split:{' '}
              {plan.overrideRoleKeys.map(roleLabel).join(', ')}.
            </p>
          )}

          <ul className="rr-staffing__problems">
            {badTotals.map((t) => (
              <li key={t}>
                {draft.variesBySize ? `${labelize(t)}: shares` : 'Shares'} add up to {totals[t]}%.
                They must add up to 100%.
              </li>
            ))}
            {unpriced.length > 0 && (
              <li>
                Set a rate above $0 for {unpriced.map(roleLabel).join(', ')} (in the roles list
                above), or untick {unpriced.length === 1 ? 'it' : 'them'}.
              </li>
            )}
            {noShare.length > 0 && (
              <li>
                Give {noShare.map(roleLabel).join(', ')} a share, or untick{' '}
                {noShare.length === 1 ? 'it' : 'them'}.
              </li>
            )}
          </ul>

          {saveError && <p className="rr-staffing__error">{saveError}</p>}

          <div className="rr-staffing__footer">
            <span className="rr-staffing__note">
              New and regenerated estimates use this split. Saved estimates keep their numbers until
              you regenerate them.
            </span>
            <div className="rr-staffing__footer-right">
              {saved && !dirty && <span className="rr-staffing__saved">Saved</span>}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDraft(draftFromPlan(plan))}
                disabled={!dirty || saving}
              >
                Reset
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSave()}
                disabled={!canSave}
              >
                {saving ? 'Saving…' : 'Save staffing'}
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export function StaffingGrid({
  productKey,
  reloadToken,
  onEditDescription,
}: {
  productKey: string;
  /** Bump to reload (e.g. after a rate changes in the roles list). */
  reloadToken?: number;
  /** `ai` plans: open a roster role's description in the roles list. */
  onEditDescription?: EditDescriptionHandler;
}) {
  const [settings, setSettings] = React.useState<StaffingSettings | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    apiFetch<{ settings: StaffingSettings }>(
      `/api/workspace/admin/staffing?productKey=${encodeURIComponent(productKey)}`
    )
      .then((res) => {
        if (!cancelled) setSettings(res.settings);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Failed to load staffing.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productKey, reloadToken]);

  return (
    <div className="rr-staffing">
      <div className="rr-section__header">
        <h3 className="rr-section__title">Who works on each deal type</h3>
        <p className="rr-section__note">
          For each deal type, pick which of the roles above work on its deals and how the hours
          split between them.
        </p>
      </div>
      {loading && !settings ? (
        <SkeletonBlock height="120px" />
      ) : loadError ? (
        <p className="rr-staffing__error">{loadError}</p>
      ) : !settings || settings.plans.length === 0 ? (
        <p className="rr-staffing__note">This product has no resource plan yet.</p>
      ) : (
        settings.plans.map((plan) => (
          <PlanTable
            key={plan.categoryKey ?? 'all'}
            plan={plan}
            settings={settings}
            productKey={productKey}
            onSaved={setSettings}
            onEditDescription={onEditDescription}
          />
        ))
      )}
    </div>
  );
}
